import {
    defineConnectorConstellationDerivation,
    defineConnectorItemDerivation,
    defineEntityConstellationDerivation,
    defineEntityItemDerivation,
    toConnectorItemCollectionDerivation,
    toEntityItemCollectionDerivation,
} from '@toolproof-core/visualization/derivation/_pure/derivation-values';
import { buildLineGuideShape, buildSphereShape } from '@toolproof-core/visualization/_pure/shapes';
import type {
    ConnectorConstellationDerivation,
    ConnectorItemCollectionDerivation,
    EntityConstellationDerivation,
    EntityItemCollectionDerivation,
} from '@toolproof-core/visualization/derivation/_contract/derivation';

import {
    toPrimitiveAxisRodCollectionKey,
    toPrimitiveAxisRodConstellationKey,
    toPrimitiveAxisRodItemKey,
    toPrimitiveAxisRodMarkerCollectionKey,
    toPrimitiveAxisRodMarkerConstellationKey,
    toPrimitiveAxisRodMarkerItemKey,
} from '@/adapters/primitive/_pure/derivation/keyCodec';
import { toDepthOrderedWheels } from '@/adapters/primitive/_pure/derivation/wheels';
import type { Config, PrimitiveWheelState } from '@/adapters/primitive/_contract/config';

type AxisRodDerivations = {
    entityItemCollectionDerivations: EntityItemCollectionDerivation[];
    entityConstellationDerivations: EntityConstellationDerivation[];
    connectorItemCollectionDerivation: ConnectorItemCollectionDerivation;
    connectorConstellationDerivation: ConnectorConstellationDerivation;
};

function buildMarkerDerivations(args: {
    config: Config;
    endpoint: 'front' | 'back';
    position: [number, number, number];
}): { collection: EntityItemCollectionDerivation; constellation: EntityConstellationDerivation } {
    const itemKey = toPrimitiveAxisRodMarkerItemKey({ endpoint: args.endpoint });
    return {
        collection: toEntityItemCollectionDerivation({
            itemCollectionKey: toPrimitiveAxisRodMarkerCollectionKey({ endpoint: args.endpoint }),
            itemDerivations: [
                defineEntityItemDerivation({
                    item: {
                        itemKind: 'entity',
                        itemKey,
                        name: `Primitive axis rod ${args.endpoint} marker`,
                        description: 'Invisible anchor for the Primitive Space center rod.',
                    },
                    parametricState: {
                        shape: buildSphereShape({ radius: 0.02 }),
                        visualStyle: { opacity: 0 },
                    },
                }),
            ],
        }),
        constellation: defineEntityConstellationDerivation({
            constellation: {
                constellationKind: 'entity',
                constellationKey: toPrimitiveAxisRodMarkerConstellationKey({ endpoint: args.endpoint }),
                itemCollectionKey: toPrimitiveAxisRodMarkerCollectionKey({ endpoint: args.endpoint }),
                anchor: { itemKey, anchorKey: 'center' },
            },
            parametricState: {
                layout: {
                    layoutKind: 'sector-ring',
                    anchor: { anchorKind: 'position', position: args.position },
                    innerRadius: 0,
                    outerRadius: 0.01,
                    angularGapFraction: 0,
                    arcSegments: args.config.base.display.wheel.arcSegments,
                    rotation: [0, 0, 0],
                },
            },
        }),
    };
}

export function buildPrimitiveAxisRodDerivations(
    config: Config,
    wheels: readonly PrimitiveWheelState[],
): AxisRodDerivations | null {
    if (!config.natural.display.marker.axisRod.visible) return null;

    // Concept wheels play no operation role, so no shared axis joins them.
    const stationary = toDepthOrderedWheels(wheels.filter((wheel) => (
        wheel.variant === undefined && !wheel.roleless
    )).map((wheel) => wheel.comparisonOrbitCenter === undefined
        ? wheel
        : { ...wheel, position: [...wheel.comparisonOrbitCenter] }));
    const frontWheel = stationary[0];
    const backWheel = stationary.at(-1);
    if (frontWheel === undefined || backWheel === undefined || frontWheel === backWheel) return null;

    const front = buildMarkerDerivations({
        config,
        endpoint: 'front',
        position: [...frontWheel.position] as [number, number, number],
    });
    const back = buildMarkerDerivations({
        config,
        endpoint: 'back',
        position: [...backWheel.position] as [number, number, number],
    });
    const frontKey = toPrimitiveAxisRodMarkerItemKey({ endpoint: 'front' });
    const backKey = toPrimitiveAxisRodMarkerItemKey({ endpoint: 'back' });

    return {
        entityItemCollectionDerivations: [front.collection, back.collection],
        entityConstellationDerivations: [front.constellation, back.constellation],
        connectorItemCollectionDerivation: toConnectorItemCollectionDerivation({
            itemCollectionKey: toPrimitiveAxisRodCollectionKey(),
            itemDerivations: [
                defineConnectorItemDerivation({
                    item: {
                        itemKind: 'connector',
                        itemKey: toPrimitiveAxisRodItemKey(),
                        shape: buildLineGuideShape(),
                        endpoints: {
                            from: { endpointKind: 'static', itemKey: frontKey, anchorKey: 'center' },
                            to: { endpointKind: 'static', itemKey: backKey, anchorKey: 'center' },
                        },
                    },
                    parametricState: {
                        visualStyle: {
                            color: config.natural.display.marker.axisRod.color,
                            opacity: config.natural.display.marker.axisRod.opacity,
                        },
                        depthTest: false,
                    },
                }),
            ],
        }),
        connectorConstellationDerivation: defineConnectorConstellationDerivation({
            constellation: {
                constellationKind: 'connector',
                constellationKey: toPrimitiveAxisRodConstellationKey(),
                itemCollectionKey: toPrimitiveAxisRodCollectionKey(),
            },
            parametricState: { visibility: { visible: true } },
        }),
    };
}
